import { create } from "zustand";
import { persist } from "zustand/middleware";
import * as ApiService from "@/services/ApiService";
import { getFacilityKey, getPatientKey } from "./profile";

export interface IInsImage {
  id: number;
  description: string;
  fileID: number;
  fileName: string;
  base64?: string;
  extension?: string;
}

export interface InsuranceInfo {
  id: number;
  relation: string;
  firstName: string;
  mi: string;
  lastName: string;
  dob: string;
  carrier: string;
  carrierPhone: string;
  empName: string;
  empPhone: string;
  idNum: string;
  groupNo: string;
  aa: number;
  wc: number;
  medPay: number;
  attName: string;
  attPhone: string;
  images: {
    front: IInsImage;
    back: IInsImage;
  };
}

export const INITIAL_INSURANCE: InsuranceInfo[] = [
  {
    id: 0,
    carrier: "",
    idNum: "",
    groupNo: "",
    carrierPhone: "",
    relation: "",
    firstName: "",
    mi: "",
    lastName: "",
    dob: "",
    aa: 0,
    wc: 0,
    medPay: 0,
    attName: "",
    attPhone: "",
    empName: "",
    empPhone: "",
    images: {
      front: {
        id: 0,
        description: "",
        fileID: 0,
        fileName: "",
        base64: "",
        extension: "",
      },
      back: {
        id: 0,
        description: "",
        fileID: 0,
        fileName: "",
        base64: "",
        extension: "",
      },
    },
  },
];

interface InsuranceState {
  loading: boolean;
  error: string | null;
  insuranceInfo: InsuranceInfo[] | null;
  currentInsuranceInfo: InsuranceInfo | null;
  fetchInsuranceInfo: (
    patientKey?: string,
    facilityKey?: string
  ) => Promise<InsuranceInfo[] | null | undefined>;
  updateInsuranceInfo: (
    insuranceInfo: InsuranceInfo,
    patientKey?: string,
    facilityKey?: string
  ) => Promise<unknown>;
  updateInsuranceInfoField: (
    id: string | number,
    field: string,
    value: string | number,
    patientKey?: string,
    facilityKey?: string
  ) => Promise<unknown>;
  uploadInsuranceImage: (
    insId: number,
    base64: string,
    description: string,
    back: boolean,
    extension: string,
    patientKey?: string,
    facilityKey?: string
  ) => Promise<unknown>;
  updateInsuranceImage: (
    imageId: number,
    base64: string,
    description: string,
    fileID: number,
    patientKey?: string,
    facilityKey?: string
  ) => Promise<unknown>;
  downloadInsuranceImage: (
    fileId: number,
    patientKey?: string,
    facilityKey?: string
  ) => Promise<unknown>;
  getInsuranceInfoImages: (
    insId: number,
    patientKey?: string,
    facilityKey?: string
  ) => Promise<unknown>;
  deleteInsuranceInfo: (
    insId: number,
    patientKey?: string,
    facilityKey?: string
  ) => Promise<unknown>;
  insuranceInfoYesNo: (yesNo?: boolean) => Promise<unknown>;
}

export const useInsuranceStore = create<InsuranceState>()(
  persist(
    (set) => ({
      loading: false,
      error: null,
      insuranceInfo: null,
      currentInsuranceInfo: null,

      fetchInsuranceInfo: async (
        patientKey = getPatientKey(),
        facilityKey = getFacilityKey()
      ) => {
        set({ loading: true });
        try {
          const response = (
            await ApiService.get(
              `/Portal/GetInsuranceInfo/${encodeURIComponent(facilityKey)}/${encodeURIComponent(patientKey)}`
            )
          ).data;
          const info =
            Array.isArray(response) && response.length ? response : null;
          set({ insuranceInfo: info });
          return info;
        } catch (err) {
          const errorMessage =
            err instanceof Error ? err.message : "An error occurred";
          set({ error: errorMessage });
          console.error("API Error:", err);
          throw err;
        } finally {
          set({ loading: false });
        }
      },

      updateInsuranceInfo: async (
        insuranceInfo,
        patientKey = getPatientKey(),
        facilityKey = getFacilityKey()
      ) => {
        set({ loading: true });
        try {
          const result = (
            await ApiService.post(
              `/Portal/UpdateInsuranceInfo/${encodeURIComponent(facilityKey)}/${encodeURIComponent(patientKey)}`,
              insuranceInfo
            )
          ).data;
          return result;
        } catch (err) {
          const errorMessage =
            err instanceof Error ? err.message : "An error occurred";
          set({ error: errorMessage });
          console.error("API Error:", err);
          throw err;
        } finally {
          set({ loading: false });
        }
      },

      updateInsuranceInfoField: async (
        id,
        field,
        value,
        patientKey = getPatientKey(),
        facilityKey = getFacilityKey()
      ) => {
        set({ loading: true });
        try {
          const result = (
            await ApiService.post(
              `/Portal/UpdateInsuranceInfoField/${encodeURIComponent(facilityKey)}/${encodeURIComponent(patientKey)}`,
              { id, field, value }
            )
          ).data;
          return result;
        } catch (err) {
          const errorMessage =
            err instanceof Error ? err.message : "An error occurred";
          set({ error: errorMessage });
          console.error("API Error:", err);
          throw err;
        } finally {
          set({ loading: false });
        }
      },

      uploadInsuranceImage: async (
        insId,
        base64,
        description,
        back,
        extension,
        patientKey = getPatientKey(),
        facilityKey = getFacilityKey()
      ) => {
        set({ loading: true });
        try {
          const result = (
            await ApiService.post(
              `/Portal/UploadInsuranceImage/${encodeURIComponent(facilityKey)}/${encodeURIComponent(patientKey)}/${insId}`,
              { base64, description, back, extension }
            )
          ).data;
          return result;
        } catch (err) {
          const errorMessage =
            err instanceof Error ? err.message : "An error occurred";
          set({ error: errorMessage });
          console.error("API Error:", err);
          throw err;
        } finally {
          set({ loading: false });
        }
      },

      updateInsuranceImage: async (
        imageId,
        base64,
        description,
        fileID,
        patientKey = getPatientKey(),
        facilityKey = getFacilityKey()
      ) => {
        set({ loading: true });
        try {
          const result = (
            await ApiService.post(
              `/Portal/UpdateInsuranceImage/${encodeURIComponent(facilityKey)}/${encodeURIComponent(patientKey)}/${imageId}`,
              { base64, description, fileID }
            )
          ).data;
          return result;
        } catch (err) {
          const errorMessage =
            err instanceof Error ? err.message : "An error occurred";
          set({ error: errorMessage });
          console.error("API Error:", err);
          throw err;
        } finally {
          set({ loading: false });
        }
      },

      downloadInsuranceImage: async (
        fileId,
        patientKey = getPatientKey(),
        facilityKey = getFacilityKey()
      ) => {
        try {
          return (
            await ApiService.get(
              `/Portal/DownloadInsuranceImage/${encodeURIComponent(facilityKey)}/${encodeURIComponent(patientKey)}/${fileId}`
            )
          ).data;
        } catch (err) {
          const errorMessage =
            err instanceof Error ? err.message : "An error occurred";
          set({ error: errorMessage });
          console.error("API Error:", err);
          throw err;
        }
      },

      getInsuranceInfoImages: async (
        insId,
        patientKey = getPatientKey(),
        facilityKey = getFacilityKey()
      ) => {
        try {
          const response = (
            await ApiService.get(
              `/Portal/GetInsuranceInfoImages/${encodeURIComponent(facilityKey)}/${encodeURIComponent(patientKey)}/${insId}`
            )
          ).data;
          return response;
        } catch (err) {
          const errorMessage =
            err instanceof Error ? err.message : "An error occurred";
          set({ error: errorMessage });
          console.error("API Error:", err);
          throw err;
        }
      },

      deleteInsuranceInfo: async (
        insId,
        patientKey = getPatientKey(),
        facilityKey = getFacilityKey()
      ) => {
        try {
          return (
            await ApiService.get(
              `/Portal/DeleteInsuranceInfo/${encodeURIComponent(facilityKey)}/${encodeURIComponent(patientKey)}/${insId}`
            )
          ).data;
        } catch (err) {
          const errorMessage =
            err instanceof Error ? err.message : "An error occurred";
          set({ error: errorMessage });
          console.error("API Error:", err);
          throw err;
        }
      },

      insuranceInfoYesNo: async (yesNo = true) => {
        const facilityKey = getFacilityKey();
        const patientKey = getPatientKey();
        return (
          await ApiService.get(
            `/Portal/InsuranceInfoYesNo/${encodeURIComponent(facilityKey)}/${encodeURIComponent(patientKey)}/${yesNo}`
          )
        ).data;
      },
    }),
    {
      name: "patient-insurance",
      // Do NOT persist patient insurance data (PHI) to localStorage. Forms always
      // re-fetch fresh on mount, so nothing needs to survive a reload, and this
      // prevents one patient's data leaking into another's session.
      partialize: () => ({}),
    }
  )
);
